import React, { useEffect, useState, useCallback } from 'react';
import { Plus, ArrowUp, ArrowDown, Pencil, AlertCircle, RefreshCw, Tag } from 'lucide-react';
import { toast } from 'react-toastify';

import { Card, Button, Badge, AreaChip, Skeleton } from '../ui-v2';
import { getIcon } from '../inventario/icons';
import { listAreasAdmin, moverArea, cambiarActivaArea } from './api';
import SoporteAreaDialog from './SoporteAreaDialog';

/**
 * SoporteAreas — pantalla de configuración de las áreas de incidencia.
 * Tabla CRUD: reordenar (flechas ↑↓), editar, activar/desactivar, crear.
 * Solo accesible con soporte.panel.manage (gateada en App.js).
 */
export default function SoporteAreas() {
  const [areas, setAreas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [accionId, setAccionId] = useState(null); // id en proceso (deshabilita sus botones)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [areaEdit, setAreaEdit] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const data = await listAreasAdmin();
      setAreas(data);
    } catch (e) {
      console.error('[soporte][areas] cargar', e);
      setError('No se pudieron cargar las áreas.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  async function reordenar(area, direccion) {
    setAccionId(area.id);
    try {
      await moverArea(area.id, direccion);
      await cargar();
    } catch (e) {
      console.error('[soporte][areas] reordenar', e);
      toast.error('No se pudo reordenar el área.');
    } finally {
      setAccionId(null);
    }
  }

  async function toggleActiva(area) {
    setAccionId(area.id);
    try {
      await cambiarActivaArea(area.id, !area.activa);
      await cargar();
    } catch (e) {
      console.error('[soporte][areas] toggleActiva', e);
      toast.error('No se pudo cambiar el estado del área.');
    } finally {
      setAccionId(null);
    }
  }

  function abrirCrear() { setAreaEdit(null); setDialogOpen(true); }
  function abrirEditar(area) { setAreaEdit(area); setDialogOpen(true); }

  return (
    <div className="px-6 py-6 min-h-full">
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-slate-900">Áreas de incidencia</h1>
          <p className="text-sm text-slate-500 mt-1">
            Configura las categorías que verán los usuarios al abrir un ticket. El orden aquí
            marca el orden en el formulario.
          </p>
        </div>
        <Button onClick={abrirCrear} className="shrink-0">
          <Plus className="w-4 h-4" />
          Nueva área
        </Button>
      </div>

      <Card className="p-0 overflow-hidden border-slate-200">
        {cargando ? (
          <SkeletonAreas />
        ) : error ? (
          <div className="p-8 text-center">
            <div className="text-sm text-red-700 flex items-center justify-center gap-2 mb-3">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
            <Button variant="outline" size="sm" onClick={cargar}>
              <RefreshCw className="w-3.5 h-3.5" />
              Reintentar
            </Button>
          </div>
        ) : areas.length === 0 ? (
          <EmptyAreas onNueva={abrirCrear} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5 font-medium w-24">Orden</th>
                  <th className="px-4 py-2.5 font-medium">Área</th>
                  <th className="px-4 py-2.5 font-medium">Slug</th>
                  <th className="px-4 py-2.5 font-medium">Tickets activos</th>
                  <th className="px-4 py-2.5 font-medium">Estado</th>
                  <th className="px-4 py-2.5 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {areas.map((a, idx) => {
                  const enProceso = accionId === a.id;
                  const esPrimera = idx === 0;
                  const esUltima = idx === areas.length - 1;
                  return (
                    <tr
                      key={a.id}
                      className={
                        'border-b border-slate-50 last:border-0 ' +
                        (a.activa ? '' : 'opacity-60 bg-slate-50/40')
                      }
                    >
                      {/* Orden + flechas */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-0.5">
                          <span className="text-xs text-slate-400 font-mono w-5">{a.orden}</span>
                          <div className="flex flex-col">
                            <button
                              type="button"
                              onClick={() => reordenar(a, 'up')}
                              disabled={esPrimera || enProceso}
                              title="Subir"
                              className="text-slate-400 hover:text-primary disabled:opacity-30 disabled:hover:text-slate-400"
                            >
                              {esPrimera ? <span className="block w-3.5 h-3.5" /> : <ArrowUp className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              type="button"
                              onClick={() => reordenar(a, 'down')}
                              disabled={esUltima || enProceso}
                              title="Bajar"
                              className="text-slate-400 hover:text-primary disabled:opacity-30 disabled:hover:text-slate-400"
                            >
                              {esUltima ? <span className="block w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </div>
                      </td>

                      {/* Área (chip de color) */}
                      <td className="px-4 py-3">
                        <AreaChip area={a.color || 'otros'} icon={a.icono ? React.createElement(getIcon(a.icono), { className: 'w-3 h-3' }) : null}>{a.nombre}</AreaChip>
                      </td>

                      {/* Slug */}
                      <td className="px-4 py-3">
                        <code className="text-xs text-slate-600 font-mono">{a.slug}</code>
                      </td>

                      {/* Tickets activos (0 hardcoded por backend en Fase 1) */}
                      <td className="px-4 py-3 text-slate-700">{a.tickets_activos ?? 0}</td>

                      {/* Estado */}
                      <td className="px-4 py-3">
                        {a.activa
                          ? <Badge variant="success">Activa</Badge>
                          : <Badge variant="secondary">Inactiva</Badge>}
                      </td>

                      {/* Acciones */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => abrirEditar(a)} disabled={enProceso}>
                            <Pencil className="w-3.5 h-3.5" />
                            Editar
                          </Button>
                          {a.activa ? (
                            <Button variant="outline" size="sm" onClick={() => toggleActiva(a)} disabled={enProceso}
                              className="text-red-700 border-red-200 hover:bg-red-50">
                              Desactivar
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" onClick={() => toggleActiva(a)} disabled={enProceso}
                              className="text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                              Activar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <SoporteAreaDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        area={areaEdit}
        onSaved={cargar}
      />
    </div>
  );
}

// Skeleton de la tabla: mismas columnas, 6 filas placeholder.
function SkeletonAreas() {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <th className="px-4 py-2.5 font-medium w-24">Orden</th>
            <th className="px-4 py-2.5 font-medium">Área</th>
            <th className="px-4 py-2.5 font-medium">Slug</th>
            <th className="px-4 py-2.5 font-medium">Tickets activos</th>
            <th className="px-4 py-2.5 font-medium">Estado</th>
            <th className="px-4 py-2.5 font-medium text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }).map((_, i) => (
            <tr key={i} className="border-b border-slate-50 last:border-0">
              <td className="px-4 py-3"><Skeleton className="h-4 w-10" /></td>
              <td className="px-4 py-3"><Skeleton className="h-5 w-24 rounded-full" /></td>
              <td className="px-4 py-3"><Skeleton className="h-3 w-20" /></td>
              <td className="px-4 py-3"><Skeleton className="h-3 w-6" /></td>
              <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
              <td className="px-4 py-3"><div className="flex justify-end gap-2"><Skeleton className="h-8 w-20" /><Skeleton className="h-8 w-24" /></div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Empty state (defensivo: con 9 áreas seedeadas no debería verse).
function EmptyAreas({ onNueva }) {
  return (
    <div className="py-16 text-center">
      <Tag className="w-10 h-10 mx-auto text-slate-300 mb-3" />
      <p className="text-sm font-medium text-slate-700">No hay áreas configuradas</p>
      <p className="text-sm text-slate-500 mt-1">Crea tu primera área para empezar a categorizar tickets.</p>
      <Button className="mt-4" onClick={onNueva}><Plus className="w-4 h-4" />Nueva área</Button>
    </div>
  );
}
